import { useState } from 'react'
import { Dices, Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import DiscountProductPicker from './DiscountProductPicker'
import { useNotification } from '../../contexts/NotificationContext'
import { createDiscount } from '../../services/discountService'

const KINDS = [
  { value: 'percentage', label: '% off' },
  { value: 'amount', label: '$ off' },
  { value: 'free_shipping', label: 'Free shipping' },
]

// Unambiguous alphabet (no O/0/I/1) so codes survive being read aloud.
function randomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = 'DUB'
  for (let i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)]
  return out
}

export default function DiscountFormModal({ onClose, onCreated }) {
  const notify = useNotification()
  const [code, setCode] = useState('')
  const [kind, setKind] = useState('percentage')
  const [value, setValue] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [usageLimit, setUsageLimit] = useState('')
  const [oncePerCustomer, setOncePerCustomer] = useState(true)
  // Shopify Product GIDs; empty = the code applies to every product.
  const [productIds, setProductIds] = useState([])
  const [saving, setSaving] = useState(false)

  const needsValue = kind !== 'free_shipping'

  const handleCreate = async () => {
    if (!code.trim()) return notify.error('Enter a code name')
    if (needsValue && !(Number(value) > 0)) return notify.error('Enter a discount value')
    setSaving(true)
    try {
      await createDiscount({
        code: code.trim().toUpperCase(),
        kind,
        value: needsValue ? Number(value) : undefined,
        // Date-only input → the code stays valid through the end of that day.
        endsAt: endsAt ? new Date(`${endsAt}T23:59:59`).toISOString() : null,
        usageLimit: usageLimit ? Number(usageLimit) : null,
        oncePerCustomer,
        // Free shipping covers the shipment, not line items — Shopify has no
        // product scope for it, so the selection only rides on value codes.
        productIds: needsValue ? productIds : [],
      })
      notify.success(`Code ${code.trim().toUpperCase()} created`)
      onCreated()
      onClose()
    } catch (err) {
      notify.error(err.message || 'Failed to create code')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New discount code</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="discount-code" className="mb-1.5 block">Code</Label>
            <div className="flex gap-2">
              <Input
                id="discount-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                placeholder="SUMMER25"
                autoCapitalize="characters"
                autoComplete="off"
                className="font-mono"
              />
              <Button type="button" variant="outline" size="icon" className="shrink-0"
                onClick={() => setCode(randomCode())} aria-label="Generate random code">
                <Dices className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Share link: getdub.com/discount/{code || 'CODE'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {needsValue && (
              <div>
                <Label htmlFor="discount-value" className="mb-1.5 block">
                  {kind === 'percentage' ? 'Percent off' : 'Dollars off'}
                </Label>
                <Input
                  id="discount-value"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max={kind === 'percentage' ? 100 : undefined}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={kind === 'percentage' ? '20' : '10'}
                />
              </div>
            )}
          </div>

          {needsValue && (
            <DiscountProductPicker selected={productIds} onChange={setProductIds} />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="discount-ends" className="mb-1.5 block">Expires</Label>
              <Input
                id="discount-ends"
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Blank = never expires</p>
            </div>
            <div>
              <Label htmlFor="discount-limit" className="mb-1.5 block">Total uses</Label>
              <Input
                id="discount-limit"
                type="number"
                inputMode="numeric"
                min="1"
                value={usageLimit}
                onChange={(e) => setUsageLimit(e.target.value)}
                placeholder="Unlimited"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={oncePerCustomer}
              onChange={(e) => setOncePerCustomer(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Limit to one use per customer
          </label>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>
              ) : 'Create code'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
